import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { AgxSolicitorDetails } from '@urbanx/agx-ui-components'
import {
  SolicitorDetails,
  useInvalidateUseStaffGetBuyersSolicitor,
  useStaffGetBuyersSolicitor,
  useStaffUpdateBuyersSolicitor,
} from '@urbanx/agx.campaigns.client'
import { useSelectedCampaign, useSelectedContract } from 'hooks'
import { MenuTitles, Pages } from 'constants/menu'
import { ErrorModal } from 'helpers/ErrorModal'
import { ContractSubPage } from '../ContractSubPage'
import { getBuyerDetailsSubPages } from './BuyerDetailsSubPages'
import { isDirtyEqual } from 'utils/compares'
import { SubPagePanel } from 'components/panel/SubPagePanel'
import RexDisconnectedModal from 'pages/campaigns/components/manageCampaign/RexDisconnectedModal'
import { RexDisconnectedModalFields, RexDisconnectedModalFieldValue } from 'pages/campaigns/components/manageCampaign/RexDataUpdateHelper'
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client'
import { isEqual } from 'lodash-es'

const BuyersSolicitor = () => {
  const solicitorId = useId()
  const { campaign } = useSelectedCampaign()
  const { contract } = useSelectedContract()
  const [isLoaded, setIsLoaded] = useState(false)
  const [validate, setValidate] = useState(false)
  const [openErrorModal, setOpenErrorModal] = useState(false)
  const [submitFormErrors, setSubmitFormErrors] = useState<string[]>([])
  const invalidateUseStaffGetBuyersSolicitor =
    useInvalidateUseStaffGetBuyersSolicitor()

  const {
    data: buyerSolicitor,
    isPending,
    isError,
  } = useStaffGetBuyersSolicitor(
    contract?.contractId || '',
    !!contract?.contractId
  )

  const [value, setValue] = useState<SolicitorDetails | undefined>(
    buyerSolicitor
  )
  const [openRexDisconnectedModal, setOpenRexDisconnectedModal] = useState(false)
  const [rexModalChangedFields, setRexModalChangedFields] = useState<Map<RexDisconnectedModalFields, RexDisconnectedModalFieldValue>>(new Map())


  useEffect(() => {
    setValue(buyerSolicitor)
    setIsLoaded(!isPending)
  }, [buyerSolicitor, isPending])

  useEffect(() => {
    setValidate(!isDirtyEqual(value, buyerSolicitor))
  }, [value])

  const { staffUpdateBuyersSolicitor, staffUpdateBuyersSolicitorPending } =
    useStaffUpdateBuyersSolicitor({
      onSuccess: () => { },
      onError: () => { },
    })

  const { data: rexListing, isPending: isPendingRexListing } = useGetListingByCampaignId(
    campaign?.campaignId || '', campaign?.campaignId !== undefined);

  const isFormDirty = useMemo(() => {
    return !isDirtyEqual(value, buyerSolicitor)
  }, [value, buyerSolicitor])

  useEffect(() => {
    if (!openRexDisconnectedModal) {
      setRexModalChangedFields(new Map());
    }
  }, [openRexDisconnectedModal]);

  const onSave = useCallback(
    async (success: () => void) => {
      if (!value || !contract?.contractId) return

      if (!rexListing && isPendingRexListing === false) {
        const prevSolicitor = buyerSolicitor;
        const newSolicitor = value;

        setRexModalChangedFields(prev => {
          const newMap = new Map(prev);

          if (!isEqual(prevSolicitor, newSolicitor)) {
            newMap.set(
              RexDisconnectedModalFields.BuyerSolicitor,
              newSolicitor?.solicitor?.solicitorName || ''
            );
          }
          return newMap;
        });
        setOpenRexDisconnectedModal(true);
      }

      await staffUpdateBuyersSolicitor(
        {
          body: value,
          contractId: contract.contractId,
        },
        {
          onSuccess: async (data) => {
            if (!contract?.contractId) return
            if (data?.success) {
              success()
              await invalidateUseStaffGetBuyersSolicitor(contract.contractId)
            } else {
              setSubmitFormErrors(data?.errors || [])
              setOpenErrorModal(true)
            }
          },
        }
      )
    },
    [value, contract?.contractId, staffUpdateBuyersSolicitor]
  )

  if (!isLoaded) return

  return (
    <ContractSubPage
      key={value ? solicitorId : undefined}
      pageTitle="Buyer Details"
      secondaryActiveMenu={MenuTitles.BUYERS_DETAILS}
      currentPage={Pages.BUYERS_SOLICITOR}
      subPages={getBuyerDetailsSubPages(campaign?.state)}
      onSaveChanges={onSave}
      isFormDirty={isFormDirty}
      isSaving={staffUpdateBuyersSolicitorPending}
      isError={isError}
    >
      {!isPending && value && (
        <SubPagePanel panelTitle="Buyer's Solicitor" expandable>
          <AgxSolicitorDetails
            id="BuyersSolicitorDetails"
            onValueUpdated={({ value }) => setValue(value)}
            defaultValue={value}
            label=""
            solicitorLabel="Solicitor"
            defaultConveyUSolicitorInfo={null}
            showToBeAdvisedOption
            required
            validate={validate}
          />
        </SubPagePanel>
      )}
      {openErrorModal && (
        <ErrorModal
          errorList={submitFormErrors}
          setShowModal={setOpenErrorModal}
        />
      )}
      {openRexDisconnectedModal ? (
        <RexDisconnectedModal
          setIsDialogOpen={setOpenRexDisconnectedModal}
          changedFields={rexModalChangedFields}
        />
      ) : (
        <></>
      )}
    </ContractSubPage>
  )
}

export default BuyersSolicitor
